/**
 * frontend/Chatbot.jsx
 * SoundDAO — Persona-aware AI Agent chat widget
 *
 * Renders Rex (Player), Ivy (Creator), or Atlas (Collector) based on persona prop.
 * On mount: loads conversation history; if empty, fires welcome message.
 * Supports real-time loading indicator and Markdown-safe message rendering.
 *
 * Props:
 *   user    — users table row ({ id, persona, rank_title, ... })
 *   session — Supabase auth session
 *
 * Ref methods (via forwardRef + useImperativeHandle):
 *   openWithMessage(text) — opens chat panel and pre-fills message, then sends
 */

import React, {
  useState, useEffect, useRef, useCallback,
  forwardRef, useImperativeHandle,
} from 'react';
import './Chatbot.css';
import { authFetch } from './lib/authFetch';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ── Markdown rendering for assistant messages ────────────────────────────────
// Rex (and the other agents) emit **bold** / *italic* / newlines in their
// replies.  Before Sprint 6B those rendered as literal characters.  We parse
// a minimal subset here — no external dependency, no full markdown.
//
// Safety: we escape ALL HTML first (so any <script> or <img> the model tries
// to emit becomes inert text), THEN apply the markdown replacements against
// the escaped string.  The only tags that end up in the DOM are the ones
// this function itself generates: <strong>, <em>, <br/>.
function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderMarkdown(text) {
  if (!text) return '';
  let out = escapeHtml(text);
  // Bold first (so the double-asterisk doesn't get consumed by the italic
  // pass).  Non-greedy to allow multiple spans on one line.
  out = out.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');
  // Italic — single asterisks wrapping non-asterisk content.
  out = out.replace(/\*([^*\n]+?)\*/g, '<em>$1</em>');
  // Paragraph breaks.
  out = out.replace(/\n/g, '<br/>');
  return out;
}

// ── Agent metadata ─────────────────────────────────────────────────────────────
// Sprint 6B Part 2 — gradient values re-tuned to the persistent agent header
// palette; collector emoji updated to 📊 per design spec.  Color/glow/bg
// retained for the typing indicator, input focus accent, and message bubble
// fallback tint (CSS custom properties set on the wrapper below).
const AGENTS = {
  player: {
    name:     'Rex',
    title:    'A&R Agent',
    emoji:    '🎧',
    color:    '#7F77DD',
    gradient: 'linear-gradient(135deg, #4a3fa8, #6b5fd4)',
    glow:     'rgba(127, 119, 221, 0.35)',
    bg:       'rgba(127, 119, 221, 0.12)',
  },
  creator: {
    name:     'Ivy',
    title:    'Publicist',
    emoji:    '🎤',
    color:    '#c45fa0',
    gradient: 'linear-gradient(135deg, #8b3a6b, #c45fa0)',
    glow:     'rgba(196, 95, 160, 0.35)',
    bg:       'rgba(196, 95, 160, 0.10)',
  },
  collector: {
    name:     'Atlas',
    title:    'Portfolio Analyst',
    emoji:    '📊',
    color:    '#2a8ab0',
    gradient: 'linear-gradient(135deg, #1a5a7a, #2a8ab0)',
    glow:     'rgba(42, 138, 176, 0.35)',
    bg:       'rgba(42, 138, 176, 0.10)',
  },
};

// ── Persistent agent header ──────────────────────────────────────────────────
// Shared between inline mode (top of Rex/Ivy/Atlas chat area) and modal mode
// (top of the floating Chatbot panel).  Pass `onClose` only for the modal
// variant — inline has no close action.
function AgentTabHeader({ agent, onClose }) {
  return (
    <div className="agent-tab-header" style={{ background: agent.gradient }}>
      <div className="agent-tab-header-left">
        <div className="agent-tab-header-icon">
          <span style={{ fontSize: 16, lineHeight: 1 }}>{agent.emoji}</span>
        </div>
        <div>
          <div className="agent-tab-header-name">{agent.name}</div>
          <div className="agent-tab-header-role">{agent.title.toUpperCase()}</div>
        </div>
      </div>
      <div className="agent-tab-header-right">
        <span className="agent-online-dot" aria-label="Online" title="Connected" />
        {onClose && (
          <button
            type="button"
            className="agent-tab-header-close"
            onClick={onClose}
            aria-label="Close chat"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main component (forwardRef enables openWithMessage from parent) ─────────────
const Chatbot = forwardRef(function Chatbot({ user, session, embedded, inline = false }, ref) {
  const persona   = user?.persona || 'player';
  const agent     = AGENTS[persona] || AGENTS.player;
  const user_id   = session?.user?.id || user?.id;

  const [isOpen,    setIsOpen]    = useState(!!embedded || inline);
  const [messages,  setMessages]  = useState([]);
  const [input,     setInput]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [welcomed,  setWelcomed]  = useState(false);
  const [unread,    setUnread]    = useState(0);

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const panelRef       = useRef(null);
  // Synchronous guard against double-init — `welcomed` state is async and
  // doesn't catch StrictMode's double effect-fire, which would otherwise
  // POST two welcome messages and render two greetings.
  const initRan        = useRef(false);

  // ── Expose openWithMessage to parent via ref ──────────────────────────────────
  useImperativeHandle(ref, () => ({
    openWithMessage(text) {
      setIsOpen(true);
      setInput(text || '');
      // Small delay so the panel is visible before we send
      setTimeout(() => {
        if (text) {
          // Programmatically trigger send after input is set
          sendMessageText(text);
        }
        inputRef.current?.focus();
      }, 200);
    },
  }));

  // ── iOS keyboard: keep panel above keyboard via visualViewport ───────────────
  // When the software keyboard appears on iOS/Android, window.innerHeight does
  // not shrink.  visualViewport.height reflects the true visible area.
  // We listen for resize events and adjust the panel's bottom/height inline.
  useEffect(() => {
    if (!isOpen) return;
    const vv = window.visualViewport;
    if (!vv) return;

    const adjust = () => {
      if (!panelRef.current) return;
      if (window.innerWidth > 768) return; // desktop: CSS handles it
      const BOTTOM_NAV = 60; // px — BottomNav height (matches BottomNav.css)
      const gap = 8;
      const available = vv.height - BOTTOM_NAV - gap * 2;
      panelRef.current.style.height  = `${Math.max(available, 200)}px`;
      panelRef.current.style.bottom  = `${BOTTOM_NAV + gap}px`;
      panelRef.current.style.left    = '12px';
      panelRef.current.style.right   = '12px';
      panelRef.current.style.width   = 'auto';
    };

    const reset = () => {
      if (!panelRef.current) return;
      panelRef.current.style.height = '';
      panelRef.current.style.bottom = '';
      panelRef.current.style.left   = '';
      panelRef.current.style.right  = '';
      panelRef.current.style.width  = '';
    };

    vv.addEventListener('resize', adjust);
    adjust();
    return () => {
      vv.removeEventListener('resize', adjust);
      reset();
    };
  }, [isOpen]);

  // ── Auto-scroll ──────────────────────────────────────────────────────────────
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // ── Focus input when opening ─────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 150);
      setUnread(0);
    }
  }, [isOpen]);

  // ── On mount: load history, then fire welcome if empty ──────────────────────
  useEffect(() => {
    if (!user_id || !persona || welcomed) return;
    if (initRan.current) return;     // StrictMode guard — see useRef above.
    initRan.current = true;

    async function initChat() {
      setLoading(true);
      try {
        // Load existing history
        const histRes = await authFetch(
          `${API_BASE}/api/chatbot/history/${user_id}?persona=${persona}`
        );
        if (histRes.ok) {
          const { history = [] } = await histRes.json();

          if (history.length > 0) {
            // Restore prior conversation
            setMessages(history.map(h => ({
              role:    h.role,
              content: h.content,
            })));
            setWelcomed(true);
            setLoading(false);
            return;
          }
        }

        // No history — fire welcome message
        const res = await authFetch(`${API_BASE}/api/chatbot/message`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id, persona, message: '' }),
        });

        if (res.ok) {
          const data = await res.json();
          setMessages([{ role: 'assistant', content: data.reply }]);
          if (!isOpen) setUnread(1);
        }
      } catch (err) {
        console.error('[Chatbot] init failed:', err);
      } finally {
        setLoading(false);
        setWelcomed(true);
      }
    }

    initChat();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id, persona]);

  // ── Core send logic (shared by button and openWithMessage) ───────────────────
  const sendMessageText = useCallback(async (text) => {
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await authFetch(`${API_BASE}/api/chatbot/message`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id, persona, message: text }),
      });

      if (res.ok) {
        const data = await res.json();
        const agentMsg = { role: 'assistant', content: data.reply };
        setMessages(prev => [...prev, agentMsg]);
        if (!isOpen) setUnread(n => n + 1);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Something went wrong. Try again in a moment.',
        }]);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Connection error. Check your backend and try again.',
      }]);
    } finally {
      setLoading(false);
    }
  }, [loading, user_id, persona, isOpen]);

  // ── Send message ─────────────────────────────────────────────────────────────
  const sendMessage = () => {
    const text = input.trim();
    if (text) sendMessageText(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  // Inline mode: render messages + input directly, no modal/FAB
  if (inline) {
    return (
      <div
        className="chatbot-inline"
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          '--agent-color':    agent.color,
          '--agent-gradient': agent.gradient,
          '--agent-glow':     agent.glow,
          '--agent-bg':       agent.bg,
        }}
      >
        {/* Persistent agent header — no close action in inline mode */}
        <AgentTabHeader agent={agent} />

        {/* Messages */}
        <div className="chatbot-messages" style={{ flex: 1, overflowY: 'auto' }}>
          {messages.map((msg, i) => {
            const isAssistant = msg.role === 'assistant';
            // External emoji avatar for Ivy/Atlas (non-player personas).  Rex
            // messages no longer get an external avatar — the persistent
            // AgentTabHeader above identifies the agent.
            const showExtAvatar = isAssistant && persona !== 'player';
            return (
              <div
                key={i}
                className={`chatbot-msg chatbot-msg--${msg.role}`}
              >
                {showExtAvatar && (
                  <span className="chatbot-msg-avatar">{agent.emoji}</span>
                )}
                <div className="chatbot-msg-bubble">
                  {isAssistant ? (
                    <div
                      className="chatbot-msg-content"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                    />
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            );
          })}

          {/* Loading indicator */}
          {loading && (
            <div className="chatbot-msg chatbot-msg--assistant">
              {persona !== 'player' && (
                <span className="chatbot-msg-avatar">{agent.emoji}</span>
              )}
              <div className="chatbot-msg-bubble chatbot-msg-bubble--typing">
                <span className="chatbot-dot" />
                <span className="chatbot-dot" />
                <span className="chatbot-dot" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input pinned to bottom */}
        <div className="chatbot-input-area">
          <textarea
            ref={inputRef}
            className="chatbot-input"
            placeholder={`Message ${agent.name}…`}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            rows={1}
          />
          <button
            className="chatbot-send-btn"
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            aria-label="Send message"
          >
            ↑
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ── Chat panel ── */}
      <div
        ref={panelRef}
        className={`chatbot-panel ${isOpen ? 'chatbot-panel--open' : ''}`}
        style={{
          '--agent-color':    agent.color,
          '--agent-gradient': agent.gradient,
          '--agent-glow':     agent.glow,
          '--agent-bg':       agent.bg,
        }}
      >
        {/* Persistent agent header — modal variant includes the close button */}
        <AgentTabHeader agent={agent} onClose={() => setIsOpen(false)} />

        {/* Messages */}
        <div className="chatbot-messages">
          {messages.map((msg, i) => {
            const isAssistant = msg.role === 'assistant';
            // External emoji avatar only for Ivy/Atlas — Rex relies on the
            // persistent header above for identity.
            const showExtAvatar = isAssistant && persona !== 'player';
            return (
              <div
                key={i}
                className={`chatbot-msg chatbot-msg--${msg.role}`}
              >
                {showExtAvatar && (
                  <span className="chatbot-msg-avatar">{agent.emoji}</span>
                )}
                <div className="chatbot-msg-bubble">
                  {isAssistant ? (
                    <div
                      className="chatbot-msg-content"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                    />
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            );
          })}

          {/* Loading indicator */}
          {loading && (
            <div className="chatbot-msg chatbot-msg--assistant">
              {persona !== 'player' && (
                <span className="chatbot-msg-avatar">{agent.emoji}</span>
              )}
              <div className="chatbot-msg-bubble chatbot-msg-bubble--typing">
                <span className="chatbot-dot" />
                <span className="chatbot-dot" />
                <span className="chatbot-dot" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="chatbot-input-area">
          <textarea
            ref={inputRef}
            className="chatbot-input"
            placeholder={`Message ${agent.name}…`}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            rows={1}
          />
          <button
            className="chatbot-send-btn"
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            aria-label="Send message"
          >
            ↑
          </button>
        </div>
      </div>

      {/* ── Toggle button (hidden when embedded/inline or on Rex tab) ── */}
      {!isOpen && !embedded && !inline && (
        <button
          className="chatbot-toggle"
          onClick={() => setIsOpen(true)}
          style={{
            background: agent.gradient,
            boxShadow:  `0 4px 16px ${agent.glow}`,
          }}
          aria-label={`Chat with ${agent.name}`}
        >
          <span className="chatbot-toggle-emoji">{agent.emoji}</span>
          {unread > 0 && (
            <span className="chatbot-unread-badge">{unread}</span>
          )}
        </button>
      )}
    </>
  );
});

export default Chatbot;
